import type { LearningBag } from "@paisa/types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"

// LearningBag + CategoryRule are canonical in @paisa/types now (#73 Q13). Re-export
// so existing `@/hooks/use-learning` imports keep working.
export type { CategoryRule, LearningBag } from "@paisa/types"

export interface MerchantAlias {
  pattern: string
  merchantId: string
  createdAt: string
}

export interface PersonAlias {
  pattern: string
  personId: string
  createdAt: string
}

interface LearningView {
  householdId: string
  bag: LearningBag
  version: number
  updatedAt: string
  /** false when the stored bag could not be decrypted; `bag` is then EMPTY and must not be overwritten silently (#129). */
  readable?: boolean
}

/** Thrown when the stored learning bag is unreadable; overwriting needs an explicit force reset. */
export class LearningUnreadableError extends Error {
  constructor(
    message = "Your saved learning data can't be read, so it was not changed. Reset it explicitly to start fresh.",
  ) {
    super(message)
    this.name = "LearningUnreadableError"
  }
}

interface LearningResponse {
  success: boolean
  data: LearningView | null
}

interface PutResponse {
  success: boolean
  data: LearningView
}

const EMPTY_BAG: LearningBag = {
  merchantAliases: [],
  personAliases: [],
  merchantRules: {},
  personRules: {},
}

export function useLearning() {
  return useQuery({
    queryKey: ["household-learning"],
    queryFn: async () => {
      const res = await api.get<LearningResponse>("/household-learning")
      return res.data
        ? res.data
        : ({
            householdId: "",
            bag: EMPTY_BAG,
            version: 0,
            updatedAt: new Date().toISOString(),
          } satisfies LearningView)
    },
    staleTime: 60_000,
  })
}

/**
 * Mutation that re-reads the latest bag, applies a transform, and PUTs the
 * result. Handles the optimistic-version handshake. Falls back to a single
 * retry on 409.
 */
export function useUpdateLearning() {
  const qc = useQueryClient()
  return useLearningMutation(qc, false)
}

/**
 * Explicit, user-confirmed reset of an unreadable learning bag. Replaces the
 * stored bag with an empty one (force=true). Never call without confirmation.
 */
export function useForceResetLearning() {
  const qc = useQueryClient()
  return useLearningMutation(qc, true)
}

function useLearningMutation(qc: ReturnType<typeof useQueryClient>, force: boolean) {
  async function fetchLatest(): Promise<LearningView> {
    const res = await api.get<LearningResponse>("/household-learning")
    return (
      res.data ?? {
        householdId: "",
        bag: structuredClone(EMPTY_BAG),
        version: 0,
        updatedAt: new Date().toISOString(),
      }
    )
  }

  return useMutation({
    mutationFn: async (transform: (bag: LearningBag) => LearningBag) => {
      let view = await fetchLatest()
      if (view.readable === false && !force) throw new LearningUnreadableError()
      let attempt = 0
      // One retry on stale version. Two attempts total.
      while (attempt < 2) {
        attempt += 1
        const nextBag = transform(structuredClone(view.bag))
        try {
          const res = await api.put<PutResponse>("/household-learning", {
            bag: nextBag,
            expectedVersion: view.version,
            ...(force ? { force: true } : {}),
          })
          return res.data
        } catch (err) {
          // Only a 409 (ApiError carries `status`) can be unreadable/stale; the
          // message is the discriminator between the two. Duck-typed on `status`
          // so module mocks of @/lib/api without ApiError still work (#129).
          const is409 = err instanceof Error && (err as { status?: number }).status === 409
          const msg = is409 ? err.message.toLowerCase() : ""
          // 409 unreadable → actionable error, never retried.
          if (msg.includes("unreadable")) throw new LearningUnreadableError()
          // 409 stale → re-fetch and retry once.
          const isStale = msg.includes("stale version")
          if (!isStale || attempt >= 2) throw err
          view = await fetchLatest()
        }
      }
      throw new Error("Failed to save learning bag after retry")
    },
    onSuccess: (data) => {
      qc.setQueryData(["household-learning"], data)
      if (force) qc.invalidateQueries({ queryKey: ["household-learning"] })
    },
  })
}
